import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import io from "socket.io-client";
import moment from "moment";
import queryString from "query-string";
import { useQueryClient } from "@tanstack/react-query";

import SplashScreen from "components/SplashScreen";

import NotificationsProvider from "./notifications";
import TimeTrackingProvider from "./time-tracking";

import api from "libs/api";

import "moment/locale/pl";
import { useCookies } from "react-cookie";

const AppContext = createContext();

const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = queryString.parse(search);
  const queryClient = useQueryClient();
  const [cookies, setCookie] = useCookies();

  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState("light");
  const [websites, setWebsites] = useState([]);
  const [webmasters, setWebmasters] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [task_tracking, setTaskTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [init_loading, setInitLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [sidebar_open, setSidebarOpen] = useState(false);
  const [send_notification_open, setSendNotificationOpen] = useState(false);
  const [create_task_open, setCreateTaskOpen] = useState(false);
  const [plans, setPlans] = useState([]);

  const current_website = websites?.find(
    (item) => item?._id === cookies?.[`${user?._id}-website`],
  );

  useEffect(() => {
    (async function () {
      await getUserData();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!!!user) return;

    const temp_socket = io.connect(`${process.env.REACT_APP_API}`, {
      reconnection: true,
      forceNew: true,
      withCredentials: true,
    });

    temp_socket.emit("join", user._id);
    setSocket(temp_socket);
    // eslint-disable-next-line
  }, [user]);

  const getUserData = async () => {
    try {
      const getMeData = await api.get("/user/me");

      if (getMeData?.user) {
        setUser(getMeData?.user);
        setTheme(getMeData?.user?.theme || "light");
        setWebsites(getMeData?.websites);
        setNotifications(getMeData?.notifications);
        setWebmasters(getMeData?.webmasters);
        setAdmins(getMeData?.admins);
        setTaskTracking({
          ...getMeData?.task_tracking,
        });
        setPlans(getMeData?.plans);

        const website_cookie = cookies?.[`${getMeData?.user?._id}-website`];

        if (!!!website_cookie) {
          setCookie(
            `${getMeData?.user?._id}-website`,
            getMeData?.websites?.[0]?._id,
            { maxAge: 1707109200 },
          );
        } else {
          const isWebsite = getMeData?.websites?.find(
            (item) => item?._id === website_cookie,
          );

          if (!!!isWebsite) {
            setCookie(
              `${getMeData?.user?._id}-website`,
              getMeData?.websites?.[0]?._id,
              { maxAge: 1707109200 },
            );
          }
        }

        i18n.changeLanguage(getMeData?.user?.language);
        moment.locale(getMeData?.user?.language);

        if (query?.next === "new_website") {
          navigate("/websites/new");
        }
      }
    } catch (err) {
      console.error(err);
    }

    setInitLoading(false);
    setLoading(false);
  };

  const login = ({ email, password }) =>
    new Promise(async (resolve, reject) => {
      try {
        await api.post("/auth/login", {
          email,
          password,
        });
        getUserData();
        resolve();
      } catch (err) {
        reject(err);
      }
    });

  const logout = async () => {
    try {
      await api.get("/auth/logout");

      // setUser(null);
      // setWebsites(null);
      window.location.replace("/login");
      queryClient?.clear();
    } catch (err) {
      console.error(err);
    }
  };

  useLayoutEffect(() => {
    const body = document.body;
    const changeClassName = "theme-changing";
    const className = `theme-${theme}`;

    body.classList.add(changeClassName, className);

    setTimeout(() => {
      body.classList.remove(changeClassName);
    }, 0);

    return () => body.classList.remove(className);
  }, [theme]);

  const value = useMemo(() => {
    return {
      user,
      theme,
      websites,
      notifications,
      webmasters,
      admins,
      socket,
      login,
      logout,
      current_website,
      plans_list: plans,
      getUserData,
      setUser,
      setTheme,
      setWebsites,
      setNotifications,
      task_tracking,
      sidebar_open,
      setSidebarOpen,
      setCookie,
      send_notification_open,
      setSendNotificationOpen,
      create_task_open,
      setCreateTaskOpen,
    };
    // eslint-disable-next-line
  }, [
    user,
    theme,
    websites,
    notifications,
    webmasters,
    admins,
    socket,
    current_website,
    sidebar_open,
    send_notification_open,
    create_task_open,
    plans,
  ]);

  if (!!loading) return <SplashScreen loading={!!init_loading} />;

  if (!!user) {
    if (user?.type === "webmaster" || user?.type === "admin") {
      return (
        <AppContext.Provider value={value}>
          <NotificationsProvider>
            <TimeTrackingProvider type={user.type}>
              {children}
            </TimeTrackingProvider>
          </NotificationsProvider>
        </AppContext.Provider>
      );
    } else {
      return (
        <AppContext.Provider value={value}>
          <NotificationsProvider>{children}</NotificationsProvider>
        </AppContext.Provider>
      );
    }
  } else {
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
  }
};

const useApp = () => useContext(AppContext);
export { AppContext, useApp };
export default AppProvider;
